import type {
  GridApiPremium,
  GridColDef,
  GridColumnVisibilityModel,
  GridDataSource,
  GridPaginationModel,
} from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import { useCallback, useMemo, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import GridTextHoverLink from "@entrilia/shared/components/grid/GridTextHoverLink";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import { logError } from "@entrilia/shared/logging";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import { stringifyValue } from "@entrilia/shared/utilities/stringHelper";
import { nameof } from "@entrilia/shared/utilities/typeHelper";
import type { WorkflowFieldSchemaDefinition, WorkflowInfo } from "../../../../api/types/workflowTypes";
import storage from "../../../../storage/storage";
import StepsProgress from "../../../common/StepsProgress";
import EntityValueGridCell from "../../../entityFields/EntityValueGridCell";
import { hasInteractiveGridCell } from "../../../entityFields/fieldGridDefinitions";
import WorkflowStatusTag from "../../change-requests/WorkflowStatusTag";

const staticColumnDefinitions: GridColDef<WorkflowInfo>[] = [
  {
    field: nameof<WorkflowInfo>("currentActivityIndex"),
    headerName: "Steps Completed",
    flex: 0.5,
    minWidth: 200,
    renderCell: ({ row }) => {
      const stepsCompleted = row.status === "Completed" ? row.activitiesCount : row.currentActivityIndex;
      return <StepsProgress completed={stepsCompleted} total={row.activitiesCount} />;
    },
  },
  {
    field: nameof<WorkflowInfo>("currentActivityName"),
    headerName: "Current Step",
    flex: 1,
    minWidth: 160,
    valueFormatter: (value: WorkflowInfo["currentActivityName"] | undefined) => value ?? null,
  },
  {
    field: nameof<WorkflowInfo>("status"),
    headerName: "Status",
    flex: 0.5,
    minWidth: 160,
    renderCell: ({ row }) => (
      <WorkflowStatusTag workflowStatus={row.status} requiresUserAction={row.requiresUserAction} />
    ),
  },
  {
    field: nameof<WorkflowInfo>("createdAt"),
    headerName: "Created at",
    flex: 0.5,
    minWidth: 160,
    valueFormatter: (value) => convertISODateTime(value),
  },
  {
    field: nameof<WorkflowInfo>("completedAt"),
    headerName: "Completed at",
    flex: 0.5,
    minWidth: 160,
    valueFormatter: (value) => convertISODateTime(value),
  },
];

const getColumnDefinitionFromField = (fieldDefinition: WorkflowFieldSchemaDefinition): GridColDef<WorkflowInfo> => ({
  field: fieldDefinition.id,
  headerName: fieldDefinition.displayName,
  flex: 1,
  minWidth: 160,
  renderCell: ({ row, field }) => {
    const value = row.workflowFields.values[field];
    if (value === undefined || value === null) {
      return null;
    }

    return <EntityValueGridCell type={fieldDefinition.type} value={value} />;
  },
});

const withRunLink = ({ renderCell, ...column }: GridColDef<WorkflowInfo>): GridColDef<WorkflowInfo> => ({
  ...column,
  renderCell: (params) =>
    renderCell ? (
      <GridTextHoverLink to={`${params.row.id}`}>{renderCell(params)}</GridTextHoverLink>
    ) : (
      <GridTextHoverLink to={`${params.row.id}`} text={stringifyValue(params.formattedValue)} />
    ),
});

const getColumnDefinitions = (
  workflowFieldsSchema: WorkflowFieldSchemaDefinition[],
  columnVisibilityModel?: GridColumnVisibilityModel
): GridColDef<WorkflowInfo>[] => {
  const interactiveFields = new Set(
    workflowFieldsSchema.filter(({ type }) => hasInteractiveGridCell(type)).map(({ id }) => id)
  );

  const columns = [...workflowFieldsSchema.map(getColumnDefinitionFromField), ...staticColumnDefinitions];
  const linkField = columns.find(
    ({ field }) => columnVisibilityModel?.[field] !== false && !interactiveFields.has(field)
  )?.field;

  return columns.map((column) => (column.field === linkField ? withRunLink(column) : column));
};

const gridPaginationModel: GridPaginationModel = { page: 0, pageSize: 25 };

interface Props {
  apiRef: RefObject<GridApiPremium | null>;
  dataSource: GridDataSource;
  filterId: string;
  workflowFieldsSchema: WorkflowFieldSchemaDefinition[];
  columnVisibilityModel?: GridColumnVisibilityModel;
}

const WorkflowRunsGrid = ({ apiRef, dataSource, filterId, workflowFieldsSchema, columnVisibilityModel }: Props) => {
  const [dataSourceError, setDataSourceError] = useState<Error>();

  const columns = useMemo(
    () => getColumnDefinitions(workflowFieldsSchema, columnVisibilityModel),
    [columnVisibilityModel, workflowFieldsSchema]
  );

  const handleDataSourceError = useCallback(
    (error: Error) => {
      logError(error, "[WorkflowRunsGrid]");
      setDataSourceError(error);
      storage.clearFilterState(filterId);
    },
    [filterId]
  );

  if (dataSourceError) {
    return <DataLoadingFailed title="Loading workflow runs failed" />;
  }

  return (
    <DataGrid<WorkflowInfo>
      apiRef={apiRef}
      columns={columns}
      lazyLoading
      dataSource={dataSource}
      paginationModel={gridPaginationModel}
      onDataSourceError={handleDataSourceError}
      noRowsText="No workflow runs"
      disableColumnReorder
      disableColumnSorting
      columnVisibilityModel={columnVisibilityModel}
    />
  );
};

export default WorkflowRunsGrid;
